// Plan 08-04 — protobuf wire codec for the WebSocket path.
//
// Wire envelope (versioned binary, framed):
//   [uint8 schema_version=1][protobuf-encoded Envelope bytes]
//
// MIRROR contract: this codec is byte-aligned with
// apps/backend/src/match/ws/protocol.ts. The backend `encodeStateSnapshot`
// JSON.stringify's the FULL PlayerView into the protobuf `your_state_json`
// field; this codec's `decodeStateSnapshot` reverses that operation and
// runs the B12 firewall guard before handing the parsed shape back.
//
// Pitfall 4 (RESEARCH §"Don't share a protobuf message instance across encode
// calls"): every encode call constructs a fresh Envelope via
// `runegambit.v1.Envelope.create({...})` — never reuses a singleton.
//
// Wire-version mismatch (`schema_version != 1`) throws SchemaVersionError;
// Plan 05's WS client maps that to close-code 1008 UNSUPPORTED_SCHEMA_VERSION.
// Protobuf parse failures throw ProtocolError; Plan 05 maps to close-code
// 1003 PROTOBUF_PARSE_FAIL.
//
// NY-08-04 (wire round-trip parity) is pinned at unit in `codec.test.ts`.
// NY-08-10 (B12 firewall, defense-in-depth) is pinned by
// `assertPlayerViewMatchesYourSlot` + `decodeStateSnapshot` runtime checks —
// the canonical B12 enforcement remains server-side in `projectPlayerView`.

import type { PlayerView } from '../PlayerViewSchema'
import { isPlayerView } from '../PlayerViewSchema'
import { runegambit } from './runegambit.pb.js'

/** DEC-018 envelope versioning lock. Bumping requires a new MessageKind.v2. */
export const SCHEMA_VERSION = 1 as const

/** Wire-version mismatch — Plan 05 maps to ws close-code 1008. */
export class SchemaVersionError extends Error {
  readonly code = 'UNSUPPORTED_SCHEMA_VERSION' as const
  constructor(message: string) {
    super(message)
    this.name = 'SchemaVersionError'
  }
}

/** Protobuf parse failure — Plan 05 maps to ws close-code 1003. */
export class ProtocolError extends Error {
  readonly code = 'PROTOBUF_PARSE_FAIL' as const
  constructor(message: string) {
    super(message)
    this.name = 'ProtocolError'
  }
}

/** B12 firewall violation — defense-in-depth runtime guard for NY-08-10. */
export class B12FirewallViolation extends Error {
  readonly code = 'B12_FIREWALL_VIOLATION' as const
  constructor(message: string) {
    super(message)
    this.name = 'B12FirewallViolation'
  }
}

/**
 * Encode an envelope as a versioned wire frame.
 * Pitfall 4: ALWAYS construct a fresh Envelope via `Envelope.create(...)`.
 */
export function encodeFrame(env: runegambit.v1.IEnvelope): Uint8Array {
  const message = runegambit.v1.Envelope.create(env)
  const bytes = runegambit.v1.Envelope.encode(message).finish()
  const out = new Uint8Array(1 + bytes.byteLength)
  out[0] = SCHEMA_VERSION
  out.set(bytes, 1)
  return out
}

/**
 * Decode a wire frame. Throws `SchemaVersionError` on version-byte mismatch
 * (Plan 05 maps to close-code 1008) or `ProtocolError` on protobuf parse
 * failure (Plan 05 maps to close-code 1003).
 */
export function decodeFrame(frame: Uint8Array): runegambit.v1.IEnvelope {
  if (frame.length === 0 || frame[0] !== SCHEMA_VERSION) {
    throw new SchemaVersionError(
      `wire schema_version mismatch: got ${frame.length === 0 ? '<empty>' : String(frame[0])}, expected ${SCHEMA_VERSION}`,
    )
  }
  try {
    return runegambit.v1.Envelope.decode(frame.subarray(1))
  } catch (e) {
    throw new ProtocolError(
      `protobuf parse failure: ${(e as Error).message ?? String(e)}`,
    )
  }
}

/**
 * NY-08-10 defense-in-depth: validate that a parsed PlayerView matches its
 * `your_slot` claim and is structurally well-formed.
 *
 * Canonical B12 enforcement lives server-side in `projectPlayerView` — this
 * runtime guard catches obvious tampering (e.g., a malicious frame with a
 * malformed board, or out-of-range your_slot) before it reaches the
 * renderer. We do NOT cross-check board contents against the server's seed
 * (that would require sharing the seed, which violates B12).
 */
export function assertPlayerViewMatchesYourSlot(view: PlayerView): void {
  if (
    typeof view.your_slot !== 'number' ||
    (view.your_slot !== 0 && view.your_slot !== 1)
  ) {
    throw new B12FirewallViolation(
      `your_slot out of range: ${String(view.your_slot)}`,
    )
  }
  // Plan 30-04 cleanup: Runespring (Phase 24+) eliminated the legacy
  // per-player rune-rack field from PlayerView — the unified shared field
  // replaces the per-player row. The B12-firewall length check is therefore
  // obsolete; the `board` array now
  // carries every cell and its presence is validated by isPlayerView.
  if (!Array.isArray(view.board)) {
    throw new B12FirewallViolation('board not an array')
  }
  // current_player_slot must also be 0 | 1.
  if (
    typeof view.current_player_slot !== 'number' ||
    (view.current_player_slot !== 0 && view.current_player_slot !== 1)
  ) {
    throw new B12FirewallViolation(
      `current_player_slot out of range: ${String(view.current_player_slot)}`,
    )
  }
}

/**
 * Decode a STATE_SNAPSHOT frame into a parsed `PlayerView`. Returns `null`
 * for non-STATE_SNAPSHOT frames (TURN_APPLIED / ERROR / PING / PONG).
 *
 * Throws `SchemaVersionError` on bad version byte, `ProtocolError` on parse
 * failure or missing `your_state_json`, `B12FirewallViolation` on shape
 * tampering.
 *
 * Plan 05 (WS client) calls this on every inbound frame and pushes the
 * returned PlayerView into the React state slot that drives MatchCanvas.
 */
export function decodeStateSnapshot(frame: Uint8Array): PlayerView | null {
  const env = decodeFrame(frame)
  if (env.kind !== runegambit.v1.MessageKind.STATE_SNAPSHOT) {
    return null
  }
  const stateJson = env.stateSnapshot?.yourState?.yourStateJson
  if (!stateJson) {
    throw new ProtocolError(
      'STATE_SNAPSHOT envelope missing your_state_json field',
    )
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(stateJson)
  } catch (e) {
    throw new ProtocolError(
      `STATE_SNAPSHOT your_state_json parse failure: ${(e as Error).message ?? String(e)}`,
    )
  }
  if (!isPlayerView(parsed)) {
    throw new ProtocolError(
      'STATE_SNAPSHOT your_state_json shape mismatch (not a PlayerView)',
    )
  }
  // Defense-in-depth B12 firewall (NY-08-10).
  assertPlayerViewMatchesYourSlot(parsed)
  return parsed
}
